import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Store, ShoppingBag, AlertCircle, BarChart3 } from 'lucide-react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';

interface SyncStats {
  validation_warnings?: number;
  validation_errors?: number;
  valid_count?: number;
  products_synced?: number;
}

interface ChannelSummary {
  id: string;
  name: string;
  platform: string;
  status: string;
  listings_count: number;
  active_listings_count: number;
  last_sync_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  last_sync_stats: SyncStats | null;
  consecutive_sync_failures: number;
}

interface ChannelsResponse {
  channels: ChannelSummary[];
  pagination: { total: number };
}

const PLATFORM_LABELS: Record<string, string> = {
  google_shopping: 'Google Shopping',
  facebook_shopping: 'Facebook/Instagram',
  amazon: 'Amazon',
  ebay: 'eBay',
  etsy: 'Etsy',
  walmart: 'Walmart',
  tiktok_shop: 'TikTok Shop',
};

const STATUS_COLORS: Record<string, any> = {
  active: 'green',
  inactive: 'gray',
  paused: 'yellow',
  error: 'red',
};

const SYNC_STATUS_COLORS: Record<string, any> = {
  success: 'green',
  partial: 'yellow',
  error: 'red',
};

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'Never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const MarketplaceOverview = () => {
  const [channels, setChannels] = useState<ChannelSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get<ChannelsResponse>('/admin/marketplace-channels?limit=100');
        setChannels(response.channels);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load marketplace data');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const activeChannels = channels.filter((c) => c.status === 'active').length;
  const totalListings = channels.reduce((sum, c) => sum + (c.active_listings_count || 0), 0);
  const errorChannels = channels.filter((c) => c.last_sync_status === 'error').length;
  const warningChannels = channels.filter((c) => c.last_sync_status === 'partial' || (c.last_sync_stats?.validation_warnings ?? 0) > 0).length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Marketplace</h1>
          <p className="text-gray-600 mt-1">Manage sales channels, feeds, and marketplace integrations</p>
        </div>
        <Link to="/admin/marketplace/channels/new">
          <Button variant="primary" size="md">
            <Store className="w-5 h-5 mr-2" />
            Add Channel
          </Button>
        </Link>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{error}</div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Store className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Channels</p>
              <p className="text-2xl font-bold text-gray-900">{activeChannels}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShoppingBag className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Active Listings</p>
              <p className="text-2xl font-bold text-gray-900">{totalListings}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Feed Warnings</p>
              <p className="text-2xl font-bold text-gray-900">{warningChannels}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Sync Errors</p>
              <p className="text-2xl font-bold text-gray-900">{errorChannels}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Channels Table */}
      <Card title="Channels" padding="md">
        {channels.length === 0 ? (
          <div className="text-center py-8">
            <Store className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 mb-1">No channels yet</h3>
            <p className="text-gray-600 mb-4">Connect your first sales channel to start listing products</p>
            <Link to="/admin/marketplace/channels/new">
              <Button variant="primary" size="sm">Add Channel</Button>
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Channel</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Platform</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-stone-500 uppercase">Health</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Listings</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Last Sync</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {channels.map((ch) => (
                  <tr key={ch.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <Link to={`/admin/marketplace/channels/${ch.id}`} className="font-medium text-gray-900 hover:text-blue-600">
                        {ch.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-gray-600">{PLATFORM_LABELS[ch.platform] || ch.platform}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {(() => {
                        const failures = ch.consecutive_sync_failures || 0;
                        const hasValidationErrors = (ch.last_sync_stats?.validation_errors ?? 0) > 0;
                        const hasValidationWarnings = (ch.last_sync_stats?.validation_warnings ?? 0) > 0;
                        if (ch.status !== 'active' && ch.status !== 'paused') return <span className="inline-block w-3 h-3 rounded-full bg-gray-300" title="Inactive" />;
                        if (failures >= 3 || ch.last_sync_status === 'error') return <span className="inline-block w-3 h-3 rounded-full bg-red-500" title={`${failures} consecutive failures`} />;
                        if (hasValidationErrors || ch.last_sync_status === 'partial') return <span className="inline-block w-3 h-3 rounded-full bg-yellow-400" title={`${ch.last_sync_stats?.validation_errors ?? 0} validation errors`} />;
                        if (hasValidationWarnings) return <span className="inline-block w-3 h-3 rounded-full bg-yellow-300" title={`${ch.last_sync_stats?.validation_warnings} warnings`} />;
                        return <span className="inline-block w-3 h-3 rounded-full bg-green-500" title="Healthy" />;
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_COLORS[ch.status] || 'gray'}>{ch.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-sm text-gray-600">
                      {ch.active_listings_count} / {ch.listings_count}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">{timeAgo(ch.last_sync_at)}</span>
                        {ch.last_sync_status && (
                          <Badge variant={SYNC_STATUS_COLORS[ch.last_sync_status] || 'gray'}>
                            {ch.last_sync_status}
                          </Badge>
                        )}
                      </div>
                      {ch.last_sync_stats && ((ch.last_sync_stats.validation_warnings ?? 0) > 0 || (ch.last_sync_stats.validation_errors ?? 0) > 0) && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {(ch.last_sync_stats.validation_errors ?? 0) > 0 && (
                            <span className="text-red-500 mr-2">{ch.last_sync_stats.validation_errors} errors</span>
                          )}
                          {(ch.last_sync_stats.validation_warnings ?? 0) > 0 && (
                            <span className="text-yellow-600">{ch.last_sync_stats.validation_warnings} warnings</span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Link to="/admin/marketplace/channels" className="block">
          <Card padding="md">
            <div className="flex items-center gap-3">
              <Store className="w-6 h-6 text-gray-600" />
              <div>
                <h3 className="font-semibold text-gray-900">Manage Channels</h3>
                <p className="text-sm text-gray-500">Add, configure, and monitor sales channels</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/marketplace/listings" className="block">
          <Card padding="md">
            <div className="flex items-center gap-3">
              <ShoppingBag className="w-6 h-6 text-gray-600" />
              <div>
                <h3 className="font-semibold text-gray-900">Manage Listings</h3>
                <p className="text-sm text-gray-500">View and manage product listings across channels</p>
              </div>
            </div>
          </Card>
        </Link>
        <Link to="/admin/marketplace/analytics" className="block">
          <Card padding="md">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-gray-600" />
              <div>
                <h3 className="font-semibold text-gray-900">Analytics</h3>
                <p className="text-sm text-gray-500">View performance metrics across channels</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>
    </div>
  );
};

export default MarketplaceOverview;
